/**
 * Composant Dashboard Owner - Widget intégré
 * Exploite l'endpoint /financial/owner/dashboard avec respect strict des règles
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Divider,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  AccountBalance as RevenueIcon,
  Assessment as CommissionIcon,
  Wallet as NetIcon,
  Home as ListingsIcon,
  EventNote as ReservationsIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { formatCurrency } from '../../utils/formatters';
import OwnerCurrencyStatisticsWidget from './OwnerCurrencyStatisticsWidget';
import OwnerStripeBalancesWidget from './OwnerStripeBalancesWidget';
import OwnerCashBalancesWidget from './OwnerCashBalancesWidget';
import OwnerPaymentMethodsWidget from './OwnerPaymentMethodsWidget';

const OwnerDashboardWidget = ({ currency = null, period = null }) => {
  const { t } = useTranslation('financial');
  const { user } = useAuth();
  const theme = useTheme();

  // États du composant
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // États pour les modales d'information
  const [infoModal, setInfoModal] = useState({
    open: false,
    type: '',
    currency: ''
  });

  /**
   * Charger les données du dashboard owner
   * Utilise l'endpoint /financial/owner/dashboard selon les règles strictes
   */
  const loadDashboardData = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      setError(null);

      console.log('📊 [OwnerDashboardWidget] Fetching owner dashboard data');

      // Construction des paramètres selon l'API backend
      const params = new URLSearchParams();
      if (currency) params.append('currency', currency);
      if (period?.start) params.append('period_start', period.start);
      if (period?.end) params.append('period_end', period.end);

      // Appel API avec gestion automatique de l'authentification
      const response = await api.get(`/financial/owner/dashboard?${params.toString()}`);

      if (!response || !response.data) {
        throw new Error(t('errors.unexpectedFormat', 'Format de réponse invalide du serveur'));
      }

      // Extraire les données selon la structure backend (success + data)
      let data;
      if (response.data.success && response.data.data) {
        data = response.data.data;
      } else if (response.data && typeof response.data === 'object') {
        data = response.data;
      } else {
        throw new Error(t('errors.unexpectedFormat', 'Structure de données inattendue'));
      }

      console.log('✅ [OwnerDashboardWidget] Dashboard data loaded:', data);

      // Déterminer la devise principale (celle avec le plus de revenus)
      let mainCurrency = 'EUR';
      if (data.currencies_breakdown) {
        const currencies = Object.entries(data.currencies_breakdown);
        if (currencies.length > 0) {
          // Trouver la devise avec le plus de revenus nets
          const sortedCurrencies = currencies.sort((a, b) => 
            (b[1].net_revenue || 0) - (a[1].net_revenue || 0)
          );
          mainCurrency = sortedCurrencies[0][0];
          console.log('🎯 [OwnerDashboardWidget] Main currency determined:', mainCurrency, 'with net revenue:', sortedCurrencies[0][1].net_revenue);
        }
      }

      // Ajouter la devise principale aux données
      data.main_currency = mainCurrency;

      setDashboardData(data);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('❌ [OwnerDashboardWidget] Error loading dashboard:', err);
      setError(err.message || t('errors.loadDashboardFailed', 'Erreur lors du chargement du dashboard'));
    } finally {
      setLoading(false);
    }
  }, [currency, period]);

  // Effet de chargement initial
  useEffect(() => {
    if (user) {
      loadDashboardData();
    }
  }, [user, loadDashboardData]);

  // Gestionnaire de rafraîchissement
  const handleRefresh = () => {
    loadDashboardData(false);
  };

  // Gestionnaires pour les modales d'information
  const openInfoModal = (type, currency = 'EUR') => {
    setInfoModal({
      open: true,
      type,
      currency
    });
  };

  const closeInfoModal = () => {
    setInfoModal({
      open: false,
      type: '',
      currency: ''
    });
  };

  // Contenu des modales d'information selon le type et la devise
  const getInfoContent = (type, currency) => {
    const messages = {
      totalRevenue: {
        title: t('dashboard.info.totalRevenue.title', 'Revenus Totaux'),
        content: t('dashboard.info.totalRevenue.content', 
          `Les revenus totaux représentent la somme de tous les paiements reçus dans la devise ${currency}.`
        )
      },
      netRevenue: {
        title: t('dashboard.info.netRevenue.title', 'Revenus Nets'),
        content: t('dashboard.info.netRevenue.content',
          `Les revenus nets en ${currency} correspondent au montant que vous recevez réellement après déduction des commissions de la plateforme.`
        )
      },
      commissions: {
        title: t('dashboard.info.commissions.title', 'Commissions'),
        content: t('dashboard.info.commissions.content',
          `Les commissions en ${currency} représentent les frais prélevés par la plateforme sur vos revenus.`
        )
      },
      pendingBalance: {
        title: t('dashboard.info.pendingBalance.title', 'Solde en Attente'),
        content: t('dashboard.info.pendingBalance.content',
          `Le solde en attente en ${currency} correspond aux montants des réservations confirmées mais pas encore finalisées.`
        )
      }
    };

    return messages[type] || {
      title: 'Information',
      content: t('globalStats.noData', 'Aucune information disponible.')
    };
  };

  // Composant de métrique
  const MetricCard = ({ title, value, currency: metricCurrency, icon, color = 'primary', infoType }) => (
    <Card 
      elevation={2}
      sx={{ 
        height: '100%',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[4]
        }
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Box color={theme.palette[color].main}>
            {icon}
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Chip 
              label={metricCurrency || 'EUR'} 
              size="small" 
              color={color}
              variant="outlined"
            />
            {infoType && (
              <Tooltip title={t('dashboard.infoTooltip', 'Cliquer pour plus d\'informations')}>
                <IconButton 
                  size="small" 
                  onClick={() => openInfoModal(infoType, metricCurrency)}
                  sx={{ color: theme.palette.text.secondary }}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
        <Typography variant="h6" component="div" fontWeight="bold">
          {formatCurrency(value || 0, metricCurrency || 'EUR')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
      </CardContent>
    </Card>
  );

  // Rendu en cas de chargement
  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
            <Typography variant="body2" ml={2}>
              {t('dashboard.loading', 'Chargement du dashboard...')}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Rendu en cas d'erreur
  if (error) {
    return (
      <Alert 
        severity="error" 
        action={
          <IconButton onClick={handleRefresh} size="small">
            <RefreshIcon />
          </IconButton>
        }
      >
        {error}
      </Alert>
    );
  }

  // Données par défaut si pas de données
  if (!dashboardData) {
    return (
      <Alert severity="info">
        {t('dashboard.noData', 'Aucune donnée disponible')}
      </Alert>
    );
  }

  return (
    <Card elevation={3}>
      <CardContent>
        {/* Section des statistiques par devise */}
        <OwnerCurrencyStatisticsWidget />
        
        {/* Section des paiements par méthode (Cash vs En Ligne) */}
        <OwnerPaymentMethodsWidget />
        
        {/* Section des soldes STRIPE */}
        <OwnerStripeBalancesWidget />
        
        {/* Séparateur entre les sections */}
        <Divider sx={{ mb: 2 }} />
        
        {/* Section des soldes CASH - Totalement indépendante */}
        <OwnerCashBalancesWidget />
      </CardContent>
    </Card>
  );
};

export default OwnerDashboardWidget;
